import { useState, useRef, useEffect, useContext } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import axios from 'axios';
import { AuthContext } from '../utils/AuthContext';
import API from '../utils/API';

const PLAN_PRICES = {
  individual: { monthly: '$14.99/mo', annual: '$152.99/yr' },
  workplace:  { monthly: '$74.99/mo', annual: '$764.99/yr' },
};

export default function SubscribeForm({ activePlan = 'individual', activeBilling = 'monthly' }) {
  const { setAuth } = useContext(AuthContext);
  const recaptcha = useRef();

  const [plan, setPlan] = useState(activePlan);
  const [billing, setBilling] = useState(activeBilling);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', password: '' });
  const [passwordInput, setPasswordInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [upperVerify, setUpperVerify] = useState(false);
  const [lowerVerify, setLowerVerify] = useState(false);
  const [numberVerify, setNumberVerify] = useState(false);
  const [symbolVerify, setSymbolVerify] = useState(false);
  const [lengthVerify, setLengthVerify] = useState(false);
  const passwordValid = upperVerify && lowerVerify && numberVerify && symbolVerify && lengthVerify;

  useEffect(() => { setPlan(activePlan); }, [activePlan]);
  useEffect(() => { setBilling(activeBilling); }, [activeBilling]);

  const onChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const validatePassword = (e) => {
    const val = e.target.value;
    setPasswordInput(val);
    const u = /[A-Z]/.test(val);
    const l = /[a-z]/.test(val);
    const n = /[0-9]/.test(val);
    const s = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(val);
    const len = val.length >= 10;
    setUpperVerify(u);
    setLowerVerify(l);
    setNumberVerify(n);
    setSymbolVerify(s);
    setLengthVerify(len);
    if (u && l && n && s && len) setForm((prev) => ({ ...prev, password: val }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!passwordValid) { setError('Password does not meet all requirements.'); return; }

    const captchaValue = recaptcha.current?.getValue();
    if (!captchaValue) { setError('Please verify the reCAPTCHA.'); return; }

    setLoading(true);
    try {
      const referral_token = localStorage.getItem('eg_ambassador_token') || undefined;
      const res = await API.registerAndCheckout({ ...form, plan_type: plan, billing, captchaValue, referral_token });
      const { user, checkout_url } = res.data;

      setAuth({ isAuthenticated: true, loading: false, token: user.token, user });
      axios.defaults.headers.common['x-auth-token'] = user.token;

      window.location.href = checkout_url;
    } catch (err) {
      const msg = err.response?.data?.errors?.[0]?.msg || 'Registration failed. Please try again.';
      setError(msg);
      recaptcha.current?.reset();
      setLoading(false);
    }
  };

  const checkIcon = (ok) => `bi bi-${ok ? 'check2-circle' : 'exclamation-circle'}`;

  return (
    <section className="pg-sf-section" id="subscribe-form">
      <div className="pg-container">
        <h2 className="pg-section-title">Create Your Account &amp; Subscribe</h2>
        <p className="pg-section-sub">
          Fill out the form below and you'll be taken directly to checkout — no charge for the first 30 days.
        </p>

        <div className="pg-trial-banner" style={{ marginBottom: '1.75rem' }}>
          <i className="bi bi-gift-fill" />
          <span>All plans include a <strong>14-day free trial</strong> — full access, no charge until your trial ends.</span>
        </div>

        <div className="pg-sf-card">
          {/* ── Plan + billing selectors ──────────────────── */}
          <div className="pg-sf-selectors">
            <div className="pg-sf-selector-group">
              <span className="pg-sf-selector-label">Plan</span>
              <div className="pg-sf-toggle">
                <button type="button" className={`pg-sf-toggle-btn ${plan === 'individual' ? 'active' : ''}`} onClick={() => setPlan('individual')}>
                  Individual
                </button>
                <button type="button" className={`pg-sf-toggle-btn ${plan === 'workplace' ? 'active' : ''}`} onClick={() => setPlan('workplace')}>
                  Workspace
                </button>
              </div>
            </div>

            <div className="pg-sf-selector-group">
              <span className="pg-sf-selector-label">Billing</span>
              <div className="pg-sf-toggle">
                <button type="button" className={`pg-sf-toggle-btn ${billing === 'monthly' ? 'active' : ''}`} onClick={() => setBilling('monthly')}>
                  Monthly
                </button>
                <button type="button" className={`pg-sf-toggle-btn ${billing === 'annual' ? 'active' : ''}`} onClick={() => setBilling('annual')}>
                  Annual <span className="pg-sf-savings">Save 15%</span>
                </button>
              </div>
            </div>

            <div className="pg-sf-price-badge">
              <span className="pg-sf-price-amount">{PLAN_PRICES[plan][billing]}</span>
            </div>
          </div>

          <div className="pg-sf-divider" />

          {/* ── Account fields ────────────────────────────── */}
          <form onSubmit={handleSubmit}>
            <div className="pg-sf-row">
              <div className="pg-sf-field">
                <label className="pg-sf-label" htmlFor="sf-first">First Name</label>
                <input id="sf-first" className="pg-sf-input" type="text" name="first_name" value={form.first_name} onChange={onChange} required />
              </div>
              <div className="pg-sf-field">
                <label className="pg-sf-label" htmlFor="sf-last">Last Name</label>
                <input id="sf-last" className="pg-sf-input" type="text" name="last_name" value={form.last_name} onChange={onChange} required />
              </div>
            </div>

            <div className="pg-sf-field">
              <label className="pg-sf-label" htmlFor="sf-email">Work Email</label>
              <input id="sf-email" className="pg-sf-input" type="email" name="email" value={form.email} onChange={onChange} required />
            </div>

            <div className="pg-sf-field">
              <label className="pg-sf-label" htmlFor="sf-password">Password</label>
              <input id="sf-password" className="pg-sf-input" type="password" name="password" value={passwordInput} onChange={validatePassword} required />
            </div>

            <div className="pg-sf-pw-req">
              <span className={lengthVerify ? 'met' : ''}><i className={checkIcon(lengthVerify)} /> 10+ characters</span>
              <span className={upperVerify ? 'met' : ''}><i className={checkIcon(upperVerify)} /> Uppercase</span>
              <span className={lowerVerify ? 'met' : ''}><i className={checkIcon(lowerVerify)} /> Lowercase</span>
              <span className={numberVerify ? 'met' : ''}><i className={checkIcon(numberVerify)} /> Number</span>
              <span className={symbolVerify ? 'met' : ''}><i className={checkIcon(symbolVerify)} /> Symbol</span>
            </div>

            {error && <p className="pg-sf-error">{error}</p>}

            <div className="pg-sf-captcha">
              <ReCAPTCHA ref={recaptcha} sitekey={process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY} />
            </div>

            <button type="submit" className="pg-sf-submit" disabled={loading || !passwordValid}>
              {loading ? 'Creating account…' : `Subscribe — ${PLAN_PRICES[plan][billing]}`}
            </button>

            <p className="pg-sf-note">
              Your 14-day free trial starts today — no charge until it ends. Subscriptions are managed through Lemon Squeezy and can be cancelled anytime.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}
